import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAARData } from "../../context/AARDataContext";
import { exportAARToExcel } from "../../utils/exportAarToExcel";
import { uploadAARFile } from "../../appwrite/appwrite";
import { useAuth } from "../../context/AuthContext";

import "./AARManagement.css";

const formatFileSize = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  return `${(bytes / 1024 ** unitIndex).toFixed(unitIndex ? 1 : 0)} ${units[unitIndex]}`;
};

function AARManagement() {

  // =====================================================

  const emptyForm = {
    title: "",
    subtitle: "",
    type: "PIPELINE",
    year: "2026",
    area: "HSSE",
    status: "DRAFT",
    location: "",
    date: "",
    overview: "",

    performance: {
      safety: "Under Review",
      quality: "Under Review",
      cost: "Under Review",
      schedule: "Under Review",
    },

    whatWentWell: [""],

    improvements: [""],

    lessonsLearned: [""],

    actions: [],
  };


  // =====================================================
  // CONTEXT
  // =====================================================

  const {
    projects,
    addAAR,
    addAARWithAppwriteFile,
    updateAAR,
    deleteAAR,
  } = useAARData();
  const { role } = useAuth();


  // =====================================================
  // PAGE STATE
  // =====================================================

  const [searchTerm, setSearchTerm] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("");

  const [yearFilter, setYearFilter] =
    useState("");

  const [showForm, setShowForm] =
    useState(false);

  const [creationMode, setCreationMode] =
    useState("none");

  const [attachmentFile, setAttachmentFile] =
    useState(null);

  const [uploadStep, setUploadStep] =
    useState("details");

  const [uploadTitle, setUploadTitle] =
    useState("");

  const [uploadActions, setUploadActions] =
    useState([]);

  const [uploadError, setUploadError] =
    useState("");

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [editingProject, setEditingProject] =
    useState(null);

  const [formData, setFormData] =
    useState(emptyForm);


  // =====================================================
  // KPI CALCULATIONS
  // =====================================================

  const totalAARs =
    projects.length;


  const published =
    projects.filter(
      (project) =>
        String(project.status || "")
          .toUpperCase() === "PUBLISHED"
    ).length;


  const underReview =
    projects.filter(
      (project) =>
        String(project.status || "")
          .toUpperCase() === "UNDER REVIEW"
    ).length;


  const drafts =
    projects.filter(
      (project) =>
        String(project.status || "")
          .toUpperCase() === "DRAFT"
    ).length;


  // =====================================================
  // FILTER PROJECTS
  // =====================================================

  const filteredProjects =
    useMemo(() => {

      const search =
        searchTerm
          .toLowerCase()
          .trim();


      return projects.filter(
        (project) => {

          const matchesSearch =
            !search ||

            String(project.title || "")
              .toLowerCase()
              .includes(search) ||

            String(project.subtitle || "")
              .toLowerCase()
              .includes(search) ||

            String(project.location || "")
              .toLowerCase()
              .includes(search) ||

            String(project.type || "")
              .toLowerCase()
              .includes(search) ||

            String(project.area || "")
              .toLowerCase()
              .includes(search);


          const matchesStatus =
            !statusFilter ||

            String(project.status || "")
              .toUpperCase() ===
              statusFilter.toUpperCase();


          const matchesYear =
            !yearFilter ||

            String(project.year || "") ===
              String(yearFilter);


          return (
            matchesSearch &&
            matchesStatus &&
            matchesYear
          );

        }
      );

    }, [
      projects,
      searchTerm,
      statusFilter,
      yearFilter,
    ]);


  // =====================================================
  // BASIC INPUT
  // =====================================================

  function handleChange(event) {

    const {
      name,
      value,
    } = event.target;


    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

  }


  // =====================================================
  // PERFORMANCE INPUT
  // =====================================================

  function handlePerformanceChange(
    event
  ) {

    const {
      name,
      value,
    } = event.target;


    setFormData((previous) => ({
      ...previous,

      performance: {
        ...previous.performance,
        [name]: value,
      },

    }));

  }


  // =====================================================
  // LIST INPUT
  // =====================================================

  function handleListChange(
    field,
    index,
    value
  ) {

    setFormData((previous) => {

      const updatedList = [
        ...(previous[field] || []),
      ];


      updatedList[index] = value;


      return {
        ...previous,
        [field]: updatedList,
      };

    });

  }


  // =====================================================
  // ADD LIST ITEM
  // =====================================================

  function addListItem(field) {

    setFormData((previous) => ({
      ...previous,

      [field]: [
        ...(previous[field] || []),
        "",
      ],

    }));

  }


  // =====================================================
  // REMOVE LIST ITEM
  // =====================================================

  function removeListItem(
    field,
    index
  ) {

    setFormData((previous) => {

      const updatedList = [
        ...(previous[field] || []),
      ];


      updatedList.splice(index, 1);


      return {
        ...previous,

        [field]:
          updatedList.length > 0
            ? updatedList
            : [""],
      };

    });

  }


  // =====================================================
  // ACTION INPUT
  // =====================================================

  function handleActionChange(
    index,
    field,
    value
  ) {

    setFormData((previous) => {

      const updatedActions = [
        ...(previous.actions || []),
      ];


      updatedActions[index] = {
        ...updatedActions[index],
        [field]: value,
      };


      return {
        ...previous,
        actions: updatedActions,
      };

    });

  }


  // =====================================================
  // ADD ACTION
  // =====================================================

  function addAction() {

    setFormData((previous) => ({

      ...previous,

      actions: [

        ...(previous.actions || []),

        {
          id:
            `aar-action-${Date.now()}-${Math.random()
              .toString(36)
              .substring(2, 8)}`,

          action: "",

          owner: "",

          dueDate: "",

          priority: "MEDIUM",

          status: "OPEN",
        },

      ],

    }));

  }


  // =====================================================
  // REMOVE ACTION
  // =====================================================

  function removeAction(index) {

    setFormData((previous) => {

      const updatedActions = [
        ...(previous.actions || []),
      ];


      updatedActions.splice(index, 1);


      return {
        ...previous,
        actions: updatedActions,
      };

    });

  }


  // =====================================================
  // NEW AAR
  // =====================================================

  function handleNewAAR() {

    setEditingProject(null);


    setFormData({

      ...emptyForm,

      performance: {
        ...emptyForm.performance,
      },

      whatWentWell: [""],

      improvements: [""],

      lessonsLearned: [""],

      actions: [],

    });


    setShowForm(false);
    setCreationMode("choice");
    setAttachmentFile(null);
    setUploadStep("details");
    setUploadTitle("");
    setUploadActions([]);
    setUploadError("");


    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

  }

  function handleChooseForm() {
    setCreationMode("form");
    setShowForm(true);
  }

  function handleChooseUpload() {
    setCreationMode("upload");
    setShowForm(false);
    setUploadStep("details");
    setUploadError("");
  }

  function handleAppwriteFileChange(event) {
    const [file] = event.target.files;
    setAttachmentFile(
      file
        ? {
            file,
            name: file.name,
            type: file.type || "Document",
            size: file.size,
          }
        : null
    );
    event.target.value = "";
  }

  function updateUploadAction(index, field, value) {
    setUploadActions((currentActions) =>
      currentActions.map((action, actionIndex) =>
        actionIndex === index ? { ...action, [field]: value } : action
      )
    );
  }

  function addUploadAction() {
    setUploadActions((currentActions) => [
      ...currentActions,
      {
        id: `aar-action-${Date.now()}-${currentActions.length}`,
        action: "",
        owner: "",
        department: "",
        dueDate: "",
        priority: "MEDIUM",
        status: "OPEN",
      },
    ]);
  }

  function removeUploadAction(index) {
    setUploadActions((currentActions) =>
      currentActions.filter((_, actionIndex) => actionIndex !== index)
    );
  }

  async function handleUploadSubmit(event) {
    event.preventDefault();
    const trimmedTitle = uploadTitle.trim();

    if (!trimmedTitle) {
      setUploadError("AAR Title is required.");
      return;
    }

    if (!attachmentFile?.file) {
      setUploadError("Please select an AAR document.");
      return;
    }

    setIsSubmitting(true);
    setUploadError("");

    try {
      const appwriteFile = await uploadAARFile(attachmentFile.file);

      await addAARWithAppwriteFile(
        {
          title: trimmedTitle,
          subtitle: "Uploaded AAR document",
          type: "OTHER",
          year: String(new Date().getFullYear()),
          area: "Execution",
          status: role === "admin" ? "PUBLISHED" : "UNDER REVIEW",
          location: "",
          date: new Date().toISOString().slice(0, 10),
          overview: "AAR registered from an uploaded document.",
          performance: {
            safety: "Under Review",
            quality: "Under Review",
            cost: "Under Review",
            schedule: "Under Review",
          },
          whatWentWell: [],
          improvements: [],
          lessonsLearned: [],
          actions: uploadActions.filter((action) => action.action.trim()),
          image: "/image 1.jpeg",
          tags: [],
        },
        appwriteFile
      );

      alert("AAR submitted successfully.");
      handleCancel();
    } catch {
      setUploadError("The AAR could not be submitted. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }


  // =====================================================
  // EDIT AAR
  // =====================================================

  function handleEdit(project) {

    setEditingProject(project);


    setFormData({

      title:
        project.title || "",

      subtitle:
        project.subtitle || "",

      type:
        project.type || "PIPELINE",

      year:
        project.year || "2026",

      area:
        project.area || "HSSE",

      status:
        project.status || "DRAFT",

      location:
        project.location || "",

      date:
        project.date || "",

      overview:
        project.overview || "",


      performance: {

        safety:
          project.performance?.safety ||
          "Under Review",

        quality:
          project.performance?.quality ||
          "Under Review",

        cost:
          project.performance?.cost ||
          "Under Review",

        schedule:
          project.performance?.schedule ||
          "Under Review",

      },


      whatWentWell:
        project.whatWentWell?.length
          ? [...project.whatWentWell]
          : [""],


      improvements:
        project.improvements?.length
          ? [...project.improvements]
          : [""],


      lessonsLearned:
        project.lessonsLearned?.length
          ? [...project.lessonsLearned]
          : [""],


      actions:
        Array.isArray(project.actions)
          ? project.actions.map(
              (action, index) => ({

                id:
                  action.id ||
                  `aar-action-${project.id}-${index}`,

                action:
                  action.action || "",

                owner:
                  action.owner || "",

                dueDate:
                  action.dueDate || "",

                priority:
                  action.priority || "MEDIUM",

                status:
                  action.status || "OPEN",

              })
            )
          : [],

    });


    setShowForm(true);


    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

  }


  // =====================================================
  // SAVE AAR
  // =====================================================

  async function handleSubmit(event) {

    event.preventDefault();


    // ---------------------------------------------------
    // VALIDATION
    // ---------------------------------------------------

    if (
      !formData.title.trim()
    ) {

      alert(
        "Please enter the AAR project title."
      );

      return;
    }


    if (
      !String(formData.year).trim()
    ) {

      alert(
        "Please enter the project year."
      );

      return;
    }


    // ---------------------------------------------------
    // CLEAN LISTS
    // ---------------------------------------------------

    const cleanedWhatWentWell =
      (formData.whatWentWell || [])
        .filter(
          (item) =>
            String(item).trim() !== ""
        );


    const cleanedImprovements =
      (formData.improvements || [])
        .filter(
          (item) =>
            String(item).trim() !== ""
        );


    const cleanedLessons =
      (formData.lessonsLearned || [])
        .filter(
          (item) =>
            String(item).trim() !== ""
        );


    // ---------------------------------------------------
    // CLEAN ACTIONS
    // ---------------------------------------------------

    const cleanedActions =
      (formData.actions || [])
        .filter(
          (action) =>
            action.action &&
            action.action.trim() !== ""
        )
        .map(
          (action, index) => ({

            id:
              action.id ||
              `aar-action-${Date.now()}-${index}`,

            action:
              action.action.trim(),

            owner:
              action.owner
                ? action.owner.trim()
                : "",

            dueDate:
              action.dueDate || "",

            priority:
              action.priority || "MEDIUM",

            status:
              action.status || "OPEN",

          })
        );


    // ---------------------------------------------------
    // NORMALIZED PROJECT
    // ---------------------------------------------------

    const projectData = {

      title:
        formData.title.trim(),

      subtitle:
        formData.subtitle.trim(),

      type:
        formData.type,

      year:
        formData.year,

      area:
        formData.area,

      status:
        role === "admin" ? formData.status : "UNDER REVIEW",

      location:
        formData.location.trim(),

      date:
        formData.date,

      overview:
        formData.overview.trim(),


      performance: {

        ...formData.performance,

      },


      whatWentWell:
        cleanedWhatWentWell,


      improvements:
        cleanedImprovements,


      lessonsLearned:
        cleanedLessons,


      actions:
        cleanedActions,

    };


    // =================================================
    // UPDATE EXISTING
    // =================================================

    if (editingProject) {

      const updatedProject = {

        ...editingProject,

        ...projectData,

      };


      try {
        await updateAAR(updatedProject);
      } catch {
        alert("AAR could not be saved to Firestore.");
        return;
      }


      alert(
        "AAR saved successfully."
      );

    }


    // =================================================
    // CREATE NEW
    // =================================================

    else {

      const newProject = {

        ...projectData,

        image:
          "/image 1.jpeg",

        tags: [],

      };


      try {
        if (attachmentFile?.file) {
          const appwriteFile = await uploadAARFile(attachmentFile.file);
          await addAARWithAppwriteFile(
            newProject,
            appwriteFile
          );
        } else {
          await addAAR(newProject);
        }
      } catch {
        alert("AAR could not be saved to Firestore.");
        return;
      }


      alert(
        "AAR saved successfully."
      );

    }


    // =================================================
    // RESET FORM
    // =================================================

    setShowForm(false);
    setCreationMode("none");
    setEditingProject(null);
    setAttachmentFile(null);

    setFormData({
      ...emptyForm,

      performance: {
        ...emptyForm.performance,
      },

      whatWentWell: [""],

      improvements: [""],

      lessonsLearned: [""],

      actions: [],

    });

  }


  // =====================================================
  // DELETE
  // =====================================================

  function handleDelete(id) {

    const project =
      projects.find(
        (item) =>
          item.id === id
      );


    if (!project) {
      return;
    }


    const confirmed =
      window.confirm(
        `Are you sure you want to delete "${project.title}"?`
      );


    if (!confirmed) {
      return;
    }


    deleteAAR(id);


    alert(
      "AAR deleted successfully."
    );

  }


  // =====================================================
  // CANCEL
  // =====================================================

  function handleCancel() {

    setShowForm(false);
    setCreationMode("none");

    setEditingProject(null);
    setAttachmentFile(null);
    setUploadStep("details");
    setUploadTitle("");
    setUploadActions([]);
    setUploadError("");

    setFormData({
      ...emptyForm,

      performance: {
        ...emptyForm.performance,
      },

      whatWentWell: [""],

      improvements: [""],

      lessonsLearned: [""],

      actions: [],

    });

  }


  // =====================================================
  // CLEAR FILTERS
  // =====================================================

  function clearFilters() {

    setSearchTerm("");

    setStatusFilter("");

    setYearFilter("");

  }

  function handleExportExcel() {
    const exportData = filteredProjects.length ? filteredProjects : projects;
    exportAARToExcel(exportData, "AAR-Records.xlsx");
  }


  // =====================================================
  // RENDER
  // =====================================================

  return (

    <main className="aar-management">


      {/* =================================================
          PAGE HEADER
      ================================================= */}

      <section className="management-header">

        <div className="management-container">

          <div className="management-header-content">

            <div>

              <span className="management-label">
                AFTER ACTION REVIEW
              </span>

              <h1>
                AAR Management
              </h1>

              <p>
                Create, review and manage After Action
                Review project records.
              </p>

            </div>


            <button
              type="button"
              className="new-aar-button"
              onClick={handleNewAAR}
            >
              + Create New AAR
            </button>

          </div>

        </div>

      </section>


      {/* =================================================
          KPI
      ================================================= */}

      <section className="management-kpi-section">

        <div className="management-container">

          <div className="management-kpi-grid">


            <div className="management-kpi">

              <span>
                Total AARs
              </span>

              <strong>
                {totalAARs}
              </strong>

              <small>
                Project records
              </small>

            </div>


            <div className="management-kpi">

              <span>
                Published
              </span>

              <strong>
                {published}
              </strong>

              <small>
                Published AARs
              </small>

            </div>


            <div className="management-kpi">

              <span>
                Under Review
              </span>

              <strong>
                {underReview}
              </strong>

              <small>
                Awaiting review
              </small>

            </div>


            <div className="management-kpi">

              <span>
                Drafts
              </span>

              <strong>
                {drafts}
              </strong>

              <small>
                AARs in progress
              </small>

            </div>


          </div>

        </div>

      </section>


      {/* =================================================
          MAIN CONTENT
      ================================================= */}

      <section className="management-content">

        <div className="management-container">


          {/* =================================================
              FORM
          ================================================= */}

          {creationMode === "choice" && (
            <div className="aar-form-card creation-choice-card">
              <div className="aar-form-header">
                <h2>Create New AAR</h2>
                <p>How would you like to create your AAR?</p>
              </div>

              <div className="creation-choice-grid">
                <button
                  type="button"
                  className="creation-choice"
                  onClick={handleChooseUpload}
                >
                  <strong>Upload Existing AAR</strong>
                  <span>
                    Upload a completed document, add actions if needed, and
                    submit it to the AAR Library.
                  </span>
                </button>

                <button
                  type="button"
                  className="creation-choice"
                  onClick={handleChooseForm}
                >
                  <strong>Create AAR Using Form</strong>
                  <span>
                    Complete the existing guided AAR form and optionally attach
                    a document before submitting.
                  </span>
                </button>
              </div>

              <div className="aar-form-actions">
                <button
                  type="button"
                  className="aar-cancel-button"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {creationMode === "upload" && (
            <div className="aar-form-card upload-aar-card">
              <div className="aar-form-header">
                <h2>{uploadStep === "review" ? "Review AAR" : "Upload Existing AAR"}</h2>
                <p>
                  Register an existing AAR document in the shared AAR Library.
                </p>
              </div>

              {uploadStep === "details" ? (
                <form onSubmit={(event) => {
                  event.preventDefault();
                  if (!uploadTitle.trim()) {
                    setUploadError("AAR Title is required.");
                    return;
                  }
                  if (!attachmentFile?.file) {
                    setUploadError("Please select an AAR document.");
                    return;
                  }
                  setUploadStep("review");
                  setUploadError("");
                }}>
                  <div className="form-section">
                    <div className="form-section-title">
                      <span>01</span>
                      <div>
                        <h3>AAR Document</h3>
                        <p>Select the completed AAR document to register.</p>
                      </div>
                    </div>

                    {!attachmentFile ? (
                      <label className="file-picker-button">
                        <span>Upload AAR</span>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp"
                          onChange={handleAppwriteFileChange}
                        />
                      </label>
                    ) : (
                      <div className="attachment-preview">
                        <div>
                          <strong>{attachmentFile.name}</strong>
                          <span>
                            {attachmentFile.type || "Document"}
                          </span>
                        </div>
                        <div className="attachment-actions">
                          <button
                            type="button"
                            className="management-filter-clear"
                            onClick={() => setAttachmentFile(null)}
                          >
                            Replace
                          </button>
                          <button
                            type="button"
                            className="management-filter-clear"
                            onClick={() => setAttachmentFile(null)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="form-section">
                    <div className="form-section-title">
                      <span>02</span>
                      <div>
                        <h3>AAR Title</h3>
                        <p>The title is required before this document can be submitted.</p>
                      </div>
                    </div>
                    <div className="aar-form-group full-width">
                      <label htmlFor="upload-aar-title">AAR Title *</label>
                      <input
                        id="upload-aar-title"
                        type="text"
                        value={uploadTitle}
                        onChange={(event) => setUploadTitle(event.target.value)}
                        placeholder="Enter AAR title"
                        required
                      />
                      {uploadError && <p className="form-error">{uploadError}</p>}
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="form-section-title">
                      <span>03</span>
                      <div>
                        <h3>Action Plan</h3>
                        <p>Actions are optional. You can add them now or skip this step.</p>
                      </div>
                    </div>

                    <div className="upload-action-list">
                      {uploadActions.map((action, index) => (
                        <div className="upload-action-item" key={action.id}>
                          <input
                            type="text"
                            value={action.action}
                            onChange={(event) => updateUploadAction(index, "action", event.target.value)}
                            placeholder="Action description"
                          />
                          <input
                            type="text"
                            value={action.owner}
                            onChange={(event) => updateUploadAction(index, "owner", event.target.value)}
                            placeholder="Owner"
                          />
                          <input
                            type="text"
                            value={action.department}
                            onChange={(event) => updateUploadAction(index, "department", event.target.value)}
                            placeholder="Department"
                          />
                          <input
                            type="text"
                            value={action.dueDate}
                            onChange={(event) => updateUploadAction(index, "dueDate", event.target.value)}
                            placeholder="Due date"
                          />
                          <select
                            value={action.priority}
                            onChange={(event) => updateUploadAction(index, "priority", event.target.value)}
                          >
                            <option value="HIGH">High</option>
                            <option value="MEDIUM">Medium</option>
                            <option value="LOW">Low</option>
                          </select>
                          <select
                            value={action.status}
                            onChange={(event) => updateUploadAction(index, "status", event.target.value)}
                          >
                            <option value="OPEN">Open</option>
                            <option value="IN PROGRESS">In Progress</option>
                            <option value="COMPLETED">Completed</option>
                          </select>
                          <button
                            type="button"
                            className="remove-action-button"
                            onClick={() => removeUploadAction(index)}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>

                    <button type="button" className="add-action-button" onClick={addUploadAction}>
                      + Add Action
                    </button>
                  </div>

                  <div className="aar-form-actions">
                    <button type="button" className="aar-cancel-button" onClick={handleCancel}>
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="aar-save-button"
                      disabled={!attachmentFile?.file || !uploadTitle.trim()}
                    >
                      Review AAR
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleUploadSubmit}>
                  <div className="review-summary">
                    <div><span>Title</span><strong>{uploadTitle.trim()}</strong></div>
                    <div><span>Document</span><strong>{attachmentFile?.name}</strong></div>
                    <div><span>File Type</span><strong>{attachmentFile?.type || "Document"}</strong></div>
                    <div><span>Storage</span><strong>Appwrite</strong></div>
                    <div><span>Actions</span><strong>{uploadActions.filter((action) => action.action.trim()).length}</strong></div>
                  </div>
                  {uploadError && <p className="form-error">{uploadError}</p>}
                  <div className="aar-form-actions">
                    <button type="button" className="aar-cancel-button" onClick={() => setUploadStep("details")}>
                      Back
                    </button>
                    <button type="submit" className="aar-save-button" disabled={isSubmitting}>
                      {isSubmitting ? "Submitting..." : "Submit AAR"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {showForm && (

            <div className="aar-form-card">


              <div className="aar-form-header">

                <h2>
                  {editingProject
                    ? "Edit AAR"
                    : "Create New AAR"}
                </h2>

                <p>
                  Capture the complete project
                  After Action Review.
                </p>

              </div>


              <form
                onSubmit={handleSubmit}
              >


                {/* =========================================
                    PROJECT INFORMATION
                ========================================== */}

                <div className="form-section">

                  <div className="form-section-title">

                    <span>
                      01
                    </span>

                    <div>

                      <h3>
                        Project Information
                      </h3>

                      <p>
                        Basic information about the project.
                      </p>

                    </div>

                  </div>


                  <div className="aar-form-grid">


                    <div className="aar-form-group full-width">

                      <label>
                        Project Title *
                      </label>

                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        placeholder="Enter project title"
                        required
                      />

                    </div>


                    <div className="aar-form-group full-width">

                      <label>
                        Subtitle
                      </label>

                      <input
                        type="text"
                        name="subtitle"
                        value={formData.subtitle}
                        onChange={handleChange}
                        placeholder="Enter project description"
                      />

                    </div>


                    <div className="aar-form-group">

                      <label>
                        Project Type
                      </label>

                      <select
                        name="type"
                        value={formData.type}
                        onChange={handleChange}
                      >

                        <option value="PIPELINE">
                          Pipeline
                        </option>

                        <option value="FACILITY">
                          Facility
                        </option>

                        <option value="MAINTENANCE">
                          Maintenance
                        </option>

                        <option value="CONSTRUCTION">
                          Construction
                        </option>

                        <option value="OTHER">
                          Other
                        </option>

                      </select>

                    </div>


                    <div className="aar-form-group">

                      <label>
                        Year
                      </label>

                      <input
                        type="text"
                        name="year"
                        value={formData.year}
                        onChange={handleChange}
                        placeholder="2026"
                      />

                    </div>


                    <div className="aar-form-group">

                      <label>
                        Area
                      </label>

                      <select
                        name="area"
                        value={formData.area}
                        onChange={handleChange}
                      >

                        <option value="HSSE">
                          HSSE
                        </option>

                        <option value="Quality">
                          Quality
                        </option>

                        <option value="Schedule">
                          Schedule
                        </option>

                        <option value="Cost">
                          Cost
                        </option>

                        <option value="Execution">
                          Execution
                        </option>

                      </select>

                    </div>


                    <div className="aar-form-group">

                      <label>
                        Status
                      </label>

                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                      >

                        <option value="DRAFT">
                          Draft
                        </option>

                        <option value="UNDER REVIEW">
                          Under Review
                        </option>

                        <option value="PUBLISHED">
                          Published
                        </option>

                      </select>

                    </div>


                    <div className="aar-form-group">

                      <label>
                        Location
                      </label>

                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="Project location"
                      />

                    </div>


                    <div className="aar-form-group">

                      <label>
                        Date
                      </label>

                      <input
                        type="text"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        placeholder="27 March 2026"
                      />

                    </div>


                    <div className="aar-form-group full-width">

                      <label>
                        Project Overview
                      </label>

                      <textarea
                        name="overview"
                        value={formData.overview}
                        onChange={handleChange}
                        placeholder="Describe the project and its purpose..."
                      />

                    </div>


                  </div>

                </div>


                {/* =========================================
                    PERFORMANCE
                ========================================== */}

                <div className="form-section">

                  <div className="form-section-title">

                    <span>
                      02
                    </span>

                    <div>

                      <h3>
                        Project Performance
                      </h3>

                      <p>
                        Record the overall performance
                        of the project.
                      </p>

                    </div>

                  </div>


                  <div className="aar-form-grid">


                    <div className="aar-form-group">

                      <label>
                        Safety
                      </label>

                      <select
                        name="safety"
                        value={
                          formData.performance.safety
                        }
                        onChange={
                          handlePerformanceChange
                        }
                      >

                        <option>
                          Excellent
                        </option>

                        <option>
                          Good
                        </option>

                        <option>
                          Satisfactory
                        </option>

                        <option>
                          Needs Improvement
                        </option>

                        <option>
                          Under Review
                        </option>

                      </select>

                    </div>


                    <div className="aar-form-group">

                      <label>
                        Quality
                      </label>

                      <select
                        name="quality"
                        value={
                          formData.performance.quality
                        }
                        onChange={
                          handlePerformanceChange
                        }
                      >

                        <option>
                          Excellent
                        </option>

                        <option>
                          Good
                        </option>

                        <option>
                          Satisfactory
                        </option>

                        <option>
                          Needs Improvement
                        </option>

                        <option>
                          Under Review
                        </option>

                      </select>

                    </div>


                    <div className="aar-form-group">

                      <label>
                        Cost
                      </label>

                      <select
                        name="cost"
                        value={
                          formData.performance.cost
                        }
                        onChange={
                          handlePerformanceChange
                        }
                      >

                        <option>
                          On Budget
                        </option>

                        <option>
                          Under Budget
                        </option>

                        <option>
                          Over Budget
                        </option>

                        <option>
                          Under Review
                        </option>

                      </select>

                    </div>


                    <div className="aar-form-group">

                      <label>
                        Schedule
                      </label>

                      <select
                        name="schedule"
                        value={
                          formData.performance.schedule
                        }
                        onChange={
                          handlePerformanceChange
                        }
                      >

                        <option>
                          Completed as Planned
                        </option>

                        <option>
                          Completed Early
                        </option>

                        <option>
                          Delayed
                        </option>

                        <option>
                          Under Review
                        </option>

                      </select>

                    </div>


                  </div>

                </div>


                {/* =========================================
                    WHAT WENT WELL
                ========================================== */}

                <div className="form-section">

                  <div className="form-section-title">

                    <span>
                      03
                    </span>

                    <div>

                      <h3>
                        What Went Well
                      </h3>

                      <p>
                        Capture successful practices
                        and positive outcomes.
                      </p>

                    </div>

                  </div>


                  <div className="repeatable-list">

                    {formData.whatWentWell.map(
                      (item, index) => (

                        <div
                          className="repeatable-item"
                          key={index}
                        >

                          <span>
                            {String(index + 1).padStart(
                              2,
                              "0"
                            )}
                          </span>

                          <input
                            type="text"
                            value={item}
                            onChange={(event) =>
                              handleListChange(
                                "whatWentWell",
                                index,
                                event.target.value
                              )
                            }
                            placeholder="What went well?"
                          />

                          <button
                            type="button"
                            className="remove-item"
                            onClick={() =>
                              removeListItem(
                                "whatWentWell",
                                index
                              )
                            }
                          >
                            ×
                          </button>

                        </div>

                      )
                    )}

                  </div>


                  <button
                    type="button"
                    className="add-item-button"
                    onClick={() =>
                      addListItem(
                        "whatWentWell"
                      )
                    }
                  >
                    + Add What Went Well
                  </button>

                </div>


                {/* =========================================
                    IMPROVEMENTS
                ========================================== */}

                <div className="form-section">

                  <div className="form-section-title">

                    <span>
                      04
                    </span>

                    <div>

                      <h3>
                        What Could Be Improved
                      </h3>

                      <p>
                        Identify opportunities for
                        improvement.
                      </p>

                    </div>

                  </div>


                  <div className="repeatable-list">

                    {formData.improvements.map(
                      (item, index) => (

                        <div
                          className="repeatable-item"
                          key={index}
                        >

                          <span>
                            {String(index + 1).padStart(
                              2,
                              "0"
                            )}
                          </span>

                          <input
                            type="text"
                            value={item}
                            onChange={(event) =>
                              handleListChange(
                                "improvements",
                                index,
                                event.target.value
                              )
                            }
                            placeholder="What could be improved?"
                          />

                          <button
                            type="button"
                            className="remove-item"
                            onClick={() =>
                              removeListItem(
                                "improvements",
                                index
                              )
                            }
                          >
                            ×
                          </button>

                        </div>

                      )
                    )}

                  </div>


                  <button
                    type="button"
                    className="add-item-button"
                    onClick={() =>
                      addListItem(
                        "improvements"
                      )
                    }
                  >
                    + Add Improvement
                  </button>

                </div>


                {/* =========================================
                    LESSONS LEARNED
                ========================================== */}

                <div className="form-section">

                  <div className="form-section-title">

                    <span>
                      05
                    </span>

                    <div>

                      <h3>
                        Lessons Learned
                      </h3>

                      <p>
                        Record knowledge that should
                        influence future projects.
                      </p>

                    </div>

                  </div>


                  <div className="repeatable-list">

                    {formData.lessonsLearned.map(
                      (item, index) => (

                        <div
                          className="repeatable-item"
                          key={index}
                        >

                          <span>
                            {String(index + 1).padStart(
                              2,
                              "0"
                            )}
                          </span>

                          <input
                            type="text"
                            value={item}
                            onChange={(event) =>
                              handleListChange(
                                "lessonsLearned",
                                index,
                                event.target.value
                              )
                            }
                            placeholder="Enter lesson learned"
                          />

                          <button
                            type="button"
                            className="remove-item"
                            onClick={() =>
                              removeListItem(
                                "lessonsLearned",
                                index
                              )
                            }
                          >
                            ×
                          </button>

                        </div>

                      )
                    )}

                  </div>


                  <button
                    type="button"
                    className="add-item-button"
                    onClick={() =>
                      addListItem(
                        "lessonsLearned"
                      )
                    }
                  >
                    + Add Lesson
                  </button>

                </div>


                {/* =========================================
                    ACTION TRACKER
                ========================================== */}

                <div className="form-section">

                  <div className="form-section-title">

                    <span>
                      07
                    </span>

                    <div>

                      <h3>
                        Action Tracker
                      </h3>

                      <p>
                        Assign actions resulting from
                        the AAR.
                      </p>

                    </div>

                  </div>


                  {formData.actions.length === 0 ? (

                    <div className="no-actions">

                      <p>
                        No actions have been added yet.
                      </p>

                    </div>

                  ) : (

                    <div className="action-form-list">

                      {formData.actions.map(
                        (action, index) => (

                          <div
                            className="action-form-item"
                            key={
                              action.id ||
                              index
                            }
                          >

                            <div className="action-form-number">

                              {String(index + 1).padStart(
                                2,
                                "0"
                              )}

                            </div>


                            <div className="action-form-fields">


                              {/* ACTION */}

                              <div className="aar-form-group">

                                <label>
                                  Action
                                </label>

                                <input
                                  type="text"
                                  value={
                                    action.action ||
                                    ""
                                  }
                                  onChange={(event) =>
                                    handleActionChange(
                                      index,
                                      "action",
                                      event.target.value
                                    )
                                  }
                                  placeholder="Enter improvement action"
                                />

                              </div>


                              {/* OWNER */}

                              <div className="aar-form-group">

                                <label>
                                  Owner
                                </label>

                                <input
                                  type="text"
                                  value={
                                    action.owner ||
                                    ""
                                  }
                                  onChange={(event) =>
                                    handleActionChange(
                                      index,
                                      "owner",
                                      event.target.value
                                    )
                                  }
                                  placeholder="Action owner"
                                />

                              </div>


                              {/* DUE DATE */}

                              <div className="aar-form-group">

                                <label>
                                  Due Date
                                </label>

                                <input
                                  type="text"
                                  value={
                                    action.dueDate ||
                                    ""
                                  }
                                  onChange={(event) =>
                                    handleActionChange(
                                      index,
                                      "dueDate",
                                      event.target.value
                                    )
                                  }
                                  placeholder="30 April 2026"
                                />

                              </div>


                              {/* PRIORITY */}

                              <div className="aar-form-group">

                                <label>
                                  Priority
                                </label>

                                <select
                                  value={
                                    action.priority ||
                                    "MEDIUM"
                                  }
                                  onChange={(event) =>
                                    handleActionChange(
                                      index,
                                      "priority",
                                      event.target.value
                                    )
                                  }
                                >

                                  <option value="HIGH">
                                    High
                                  </option>

                                  <option value="MEDIUM">
                                    Medium
                                  </option>

                                  <option value="LOW">
                                    Low
                                  </option>

                                </select>

                              </div>


                              {/* STATUS */}

                              <div className="aar-form-group">

                                <label>
                                  Status
                                </label>

                                <select
                                  value={
                                    action.status ||
                                    "OPEN"
                                  }
                                  onChange={(event) =>
                                    handleActionChange(
                                      index,
                                      "status",
                                      event.target.value
                                    )
                                  }
                                >

                                  <option value="OPEN">
                                    Open
                                  </option>

                                  <option value="IN PROGRESS">
                                    In Progress
                                  </option>

                                  <option value="COMPLETED">
                                    Completed
                                  </option>

                                </select>

                              </div>


                            </div>


                            <button
                              type="button"
                              className="remove-action-button"
                              onClick={() =>
                                removeAction(
                                  index
                                )
                              }
                            >
                              Remove
                            </button>


                          </div>

                        )
                      )}

                    </div>

                  )}


                  <button
                    type="button"
                    className="add-action-button"
                    onClick={addAction}
                  >
                    + Add Action
                  </button>


                </div>


                {/* =========================================
                    FORM BUTTONS
                ========================================== */}

                <div className="aar-form-actions">

                  {!editingProject && (
                    <div className="form-section attachment-form-section">
                    <div className="form-section-title">
                      <span>07</span>
                      <div>
                        <h3>Attachments</h3>
                        <p>Optionally attach the completed AAR or a supporting document.</p>
                      </div>
                    </div>
                    <div className="aar-form-group full-width">
                      <label htmlFor="form-drive-file">Attach AAR or supporting document</label>
                      <input
                        id="form-drive-file"
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp"
                        onChange={handleAppwriteFileChange}
                      />
                      <small className="field-help">Optional. The selected file will be uploaded to secure document storage when the AAR is submitted.</small>
                    </div>
                    {attachmentFile && (
                      <div className="attachment-preview compact">
                        <div>
                          <strong>{attachmentFile.name}</strong>
                          <span>{attachmentFile.type || "Document"} • {formatFileSize(attachmentFile.size)}</span>
                        </div>
                        <button type="button" className="management-filter-clear" onClick={() => setAttachmentFile(null)}>
                          Remove
                        </button>
                      </div>
                    )}
                    </div>
                  )}

                  <button
                    type="button"
                    className="aar-cancel-button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>


                  <button
                    type="submit"
                    className="aar-save-button"
                    disabled={!formData.title.trim()}
                  >
                    {editingProject
                      ? "Update AAR"
                      : "Create AAR"}
                  </button>

                </div>


              </form>

            </div>

          )}


          {/* =================================================
              TOOLBAR
          ================================================= */}

          {!showForm && creationMode === "none" && (

            <div className="management-toolbar">


              <div className="management-search">

                <input
                  type="text"
                  placeholder="Search AAR projects..."
                  value={searchTerm}
                  onChange={(event) =>
                    setSearchTerm(
                      event.target.value
                    )
                  }
                />

              </div>


              <select
                className="management-filter"
                value={statusFilter}
                onChange={(event) =>
                  setStatusFilter(
                    event.target.value
                  )
                }
              >

                <option value="">
                  All Statuses
                </option>

                <option value="PUBLISHED">
                  Published
                </option>

                <option value="UNDER REVIEW">
                  Under Review
                </option>

                <option value="DRAFT">
                  Draft
                </option>

              </select>


              <select
                className="management-filter"
                value={yearFilter}
                onChange={(event) =>
                  setYearFilter(
                    event.target.value
                  )
                }
              >

                <option value="">
                  All Years
                </option>

                <option value="2026">
                  2026
                </option>

                <option value="2025">
                  2025
                </option>

                <option value="2024">
                  2024
                </option>

              </select>


              {(searchTerm ||
                statusFilter ||
                yearFilter) && (

                <button
                  type="button"
                  className="management-filter-clear"
                  onClick={clearFilters}
                >
                  Clear
                </button>

              )}

              <button
                type="button"
                className="management-filter-clear"
                onClick={handleExportExcel}
              >
                Export Excel
              </button>

            </div>

          )}


          {/* =================================================
              TABLE
          ================================================= */}

          {!showForm && creationMode === "none" && (

            <div className="management-table-card">


              <div className="management-table-header">

                <h2>
                  AAR Records
                </h2>

                <span className="management-record-count">
                  {filteredProjects.length} Records
                </span>

              </div>


              {filteredProjects.length > 0 ? (

                <div className="management-table-wrapper">

                  <table className="management-table">

                    <thead>

                      <tr>

                        <th>
                          PROJECT
                        </th>

                        <th>
                          YEAR
                        </th>

                        <th>
                          AREA
                        </th>

                        <th>
                          STATUS
                        </th>

                        <th>
                          ACTIONS
                        </th>

                        <th>
                          MANAGE
                        </th>

                      </tr>

                    </thead>


                    <tbody>

                      {filteredProjects.map(
                        (project) => (

                          <tr
                            key={project.id}
                          >


                            <td>

                              <div className="project-info">

                                <span className="project-type">
                                  {project.type ||
                                    "PROJECT"}
                                </span>

                                <span className="project-title">
                                  {project.title ||
                                    "Untitled Project"}
                                </span>

                                <span className="project-subtitle">
                                  {project.subtitle ||
                                    "No description"}
                                </span>

                              </div>

                            </td>


                            <td>
                              {project.year ||
                                "—"}
                            </td>


                            <td>
                              {project.area ||
                                "—"}
                            </td>


                            <td>

                              <span
                                className={`management-status ${String(
                                  project.status ||
                                    ""
                                )
                                  .toLowerCase()
                                  .replace(
                                    /\s+/g,
                                    "-"
                                  )}`}
                              >
                                {project.status ||
                                  "UNKNOWN"}
                              </span>

                            </td>


                            <td>

                              <span className="action-count">

                                {Array.isArray(
                                  project.actions
                                )
                                  ? project.actions.length
                                  : 0}

                              </span>

                            </td>


                            <td>

                              <div className="management-actions">


                                <Link
                                  to={`/aar/${project.id}`}
                                  className="management-action-button"
                                >
                                  View
                                </Link>


                                {role === "admin" && (
                                  <>
                                    <button
                                      type="button"
                                      className="management-action-button edit"
                                      onClick={() => handleEdit(project)}
                                    >
                                      Edit
                                    </button>

                                    <button
                                      type="button"
                                      className="management-action-button delete"
                                      onClick={() => handleDelete(project.id)}
                                    >
                                      Delete
                                    </button>
                                  </>
                                )}


                              </div>

                            </td>


                          </tr>

                        )
                      )}

                    </tbody>

                  </table>

                </div>

              ) : (

                <div className="management-empty">

                  <div className="management-empty-icon">
                    🔍
                  </div>

                  <h3>
                    No AAR records found
                  </h3>

                  <p>
                    Try changing your search or
                    filter settings.
                  </p>

                  <button
                    type="button"
                    className="management-filter-clear"
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </button>

                </div>

              )}

            </div>

          )}


        </div>

      </section>

    </main>

  );

}


export default AARManagement;